import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchProductByCode } from '@/api/product';
import type { MhProduct } from '@/types/mh';
import ServiceInfo from './components/ServiceInfo';
import DetailTabs from './components/DetailTabs';
import QueryForm from './components/QueryForm';
import {
  formatProductPriceAmount,
  formatProductPriceUnit,
  parseProductTags,
  resolveAssetUrl,
} from '@/utils/product-display';

const PLACEHOLDER_ICON = `${import.meta.env.BASE_URL}vite.svg`;

function hasOrderFields(json?: string): boolean {
  try {
    const arr = JSON.parse(json || '[]') as unknown;
    if (!Array.isArray(arr)) {
      return false;
    }
    return arr.some((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      const key = (item as { key?: unknown }).key;
      return typeof key === 'string' && key.length > 0;
    });
  } catch {
    return false;
  }
}

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<MhProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setError('链接无效');
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchProductByCode(slug);
      if (cancelled) {
        return;
      }
      if (res.code === 0 && res.data) {
        setProduct(res.data);
        setError('');
      } else {
        setProduct(null);
        setError(res.msg || '产品不存在或已下架');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-gray-50 text-gray-500 text-sm">
          加载中…
        </div>
        <Footer />
      </div>
    );
  }

  if (!product || error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-2-line text-5xl text-gray-300" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">服务不可用</h2>
            <p className="text-gray-500 text-sm mb-6">{error || '该服务可能已下线或链接有误'}</p>
            <Link
              to="/services"
              className="px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回服务列表
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const tags = parseProductTags(product.tags);
  const info = {
    tags,
    name: product.productName,
    description: product.description?.trim() || '信息查询服务，请按右侧表单填写参数并提交订单。',
    priceAmount: formatProductPriceAmount(product.price, product),
    priceUnit: formatProductPriceUnit(product),
    icon: resolveAssetUrl(product.iconUrl) || PLACEHOLDER_ICON,
    tag: tags[0],
    rating: 5,
  };
  const mobileOrderFormId = `mobile-order-form-${product.productId}`;
  const mobileSubmitDisabled = !hasOrderFields(product.paramConfig);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-44 md:pb-6 lg:py-8">
          {/* Breadcrumb */}
          <nav className="mb-5">
            <ol className="flex items-center space-x-2 text-sm text-gray-400">
              <li>
                <Link to="/" className="hover:text-gray-700 transition-colors cursor-pointer">
                  首页
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
                </div>
                <Link to="/services" className="hover:text-gray-700 transition-colors cursor-pointer">
                  查询服务
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
                </div>
                <span className="text-gray-700 font-medium truncate max-w-[200px]">{product.productName}</span>
              </li>
            </ol>
          </nav>

          {/* Main layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: content */}
            <div className="lg:col-span-2 space-y-5">
              <ServiceInfo service={info} />
              <QueryForm
                product={product}
                className="lg:hidden"
                formId={mobileOrderFormId}
                showSubmitButton={false}
              />
              <div className="lg:hidden fixed left-0 right-0 bottom-16 z-40 bg-white/95 border-t border-gray-100 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur">
                <button
                  type="submit"
                  form={mobileOrderFormId}
                  disabled={mobileSubmitDisabled}
                  className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交订单
                </button>
              </div>
              <DetailTabs product={product} />
            </div>

            {/* Right: form (sticky) */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="sticky top-6">
                <QueryForm product={product} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
